import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { httpUrlSchema } from '../governance/topics.js';
export const TASK_STATUSES = [
    'BACKLOG',
    'PLANNED',
    'IN_PROGRESS',
    'BLOCKED',
    'IN_REVIEW',
    'DONE',
    'ARCHIVED',
    'CANCELLED',
];
export const taskStatusSchema = z.enum(TASK_STATUSES);
/** Statuses in which a task still needs work; only these can be overdue. */
export const OPEN_TASK_STATUSES = [
    'BACKLOG',
    'PLANNED',
    'IN_PROGRESS',
    'BLOCKED',
    'IN_REVIEW',
];
/** Statuses a member sets with a plain transition (DONE, BLOCKED, CANCELLED, ARCHIVED have their own flows). */
export const WORKFLOW_TASK_STATUSES = [
    'BACKLOG',
    'PLANNED',
    'IN_PROGRESS',
    'IN_REVIEW',
];
export const workflowTaskStatusSchema = z.enum(WORKFLOW_TASK_STATUSES);
export const TASK_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);
export const TASK_ORIGINS = [
    'MANUAL',
    'DECISION',
    'TOPIC',
    'MEETING',
    'WEEKLY_UPDATE',
    'AI_DRAFT_CONFIRMED',
    'RECURRENCE',
];
export const taskOriginSchema = z.enum(TASK_ORIGINS);
export function isOpenTaskStatus(status) {
    return OPEN_TASK_STATUSES.includes(status);
}
/**
 * The single overdue rule (spec §19.2: "computed consistently across web and notifications"): an
 * open task whose due instant has passed. Tasks without a deadline are never overdue.
 */
export function isTaskOverdue(task, now) {
    return (task.dueAt !== null && isOpenTaskStatus(task.status) && Date.parse(task.dueAt) < now.getTime());
}
const memberRef = z.object({ memberId: uuidSchema, displayName: z.string() });
const entityRef = z.object({ id: uuidSchema, number: z.number().int(), title: z.string() });
export const taskSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
    status: taskStatusSchema,
    priority: taskPrioritySchema,
    owner: memberRef,
    dueAt: isoDateTimeSchema.nullable(),
    noDeadline: z.boolean(),
    /** Server-computed with {@link isTaskOverdue} at response time. */
    overdue: z.boolean(),
    completedAt: isoDateTimeSchema.nullable(),
    origin: taskOriginSchema,
    decisionId: uuidSchema.nullable(),
    topicId: uuidSchema.nullable(),
    openBlockerCount: z.number().int(),
    createdAt: isoDateTimeSchema,
});
export const taskListSchema = z.object({ items: z.array(taskSummarySchema) });
export const taskPageSchema = cursorPageSchema(taskSummarySchema);
export const TASK_VIEWS = ['MINE', 'TEAM', 'WATCHING', 'CREATED'];
export const taskViewSchema = z.enum(TASK_VIEWS);
export const TASK_SORTS = ['DUE', 'RECENT'];
export const taskSortSchema = z.enum(TASK_SORTS);
const booleanQuery = z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true'));
export const taskListQuerySchema = z.object({
    cursor: z.string().min(1).max(300).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    view: taskViewSchema.default('TEAM'),
    sort: taskSortSchema.default('DUE'),
    /** Comma-separated statuses; default: open statuses only. */
    status: z
        .string()
        .optional()
        .transform((value, ctx) => {
        if (value === undefined || value === '')
            return undefined;
        const parsed = z.array(taskStatusSchema).safeParse(value.split(','));
        if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: 'Unknown task status' });
            return z.NEVER;
        }
        return parsed.data;
    }),
    ownerMemberId: uuidSchema.optional(),
    topicId: uuidSchema.optional(),
    decisionId: uuidSchema.optional(),
    overdue: booleanQuery,
    q: z.string().trim().min(2).max(200).optional(),
});
export const taskTitleSchema = z.string().trim().min(3).max(200);
const descriptionSchema = z.string().trim().max(10000).nullable().optional();
const acceptanceSchema = z.string().trim().max(5000).nullable().optional();
export const evidenceUrlSchema = httpUrlSchema;
function deadlineIsConsistent(value) {
    return !(value.noDeadline === true && value.dueAt !== undefined && value.dueAt !== null);
}
export const actionItemInputSchema = z
    .object({
    title: taskTitleSchema,
    description: descriptionSchema,
    acceptanceCriteria: acceptanceSchema,
    /** Exactly one accountable owner (spec §7.6). */
    ownerMemberId: uuidSchema,
    priority: taskPrioritySchema.default('NORMAL'),
    dueAt: isoDateTimeSchema.nullable().default(null),
    noDeadline: z.boolean().default(false),
})
    .refine(deadlineIsConsistent, {
    message: 'Choose either a due date or "no deadline"',
    path: ['noDeadline'],
})
    .refine((value) => value.noDeadline || value.dueAt !== null, {
    message: 'Set a due date or mark the action as having no deadline',
    path: ['dueAt'],
});
/** Creates follow-up actions from a FINALIZED decision; each keeps the decision link. */
export const createDecisionTasksRequestSchema = z.object({
    items: z.array(actionItemInputSchema).min(1).max(20),
    /** AI action-item suggestions these tasks were confirmed from (the suggestions become ACCEPTED). */
    aiOutputId: uuidSchema.nullable().optional(),
});
/** Manual task, optionally linked to a topic the creator can see (origin TOPIC). */
export const createTaskRequestSchema = z
    .object({
    title: taskTitleSchema,
    description: descriptionSchema,
    acceptanceCriteria: acceptanceSchema,
    ownerMemberId: uuidSchema,
    priority: taskPrioritySchema.default('NORMAL'),
    dueAt: isoDateTimeSchema.nullable().default(null),
    noDeadline: z.boolean().default(false),
    status: z.enum(['BACKLOG', 'PLANNED']).default('PLANNED'),
    topicId: uuidSchema.nullable().optional(),
    collaboratorMemberIds: z.array(uuidSchema).max(20).default([]),
})
    .refine(deadlineIsConsistent, {
    message: 'Choose either a due date or "no deadline"',
    path: ['noDeadline'],
})
    .refine((value) => value.noDeadline || value.dueAt !== null, {
    message: 'Set a due date or mark the task as having no deadline',
    path: ['dueAt'],
});
export const updateTaskRequestSchema = z
    .object({
    version: versionSchema,
    title: taskTitleSchema.optional(),
    description: descriptionSchema,
    acceptanceCriteria: acceptanceSchema,
    priority: taskPrioritySchema.optional(),
    dueAt: isoDateTimeSchema.nullable().optional(),
    noDeadline: z.boolean().optional(),
})
    .refine(deadlineIsConsistent, {
    message: 'Choose either a due date or "no deadline"',
    path: ['noDeadline'],
})
    .refine((value) => value.title !== undefined ||
    value.description !== undefined ||
    value.acceptanceCriteria !== undefined ||
    value.priority !== undefined ||
    value.dueAt !== undefined ||
    value.noDeadline !== undefined, { message: 'Nothing to update' });
export const transitionTaskRequestSchema = z.object({
    version: versionSchema,
    to: workflowTaskStatusSchema,
    note: z.string().trim().max(2000).nullable().optional(),
});
/** Spec §6.4: completion stores the timestamp and an optional outcome note and evidence link. */
export const completeTaskRequestSchema = z.object({
    version: versionSchema,
    outcomeNote: z.string().trim().max(5000).nullable().optional(),
    evidenceUrl: evidenceUrlSchema.nullable().optional(),
});
export const reasonedTaskRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const archiveTaskRequestSchema = z.object({ version: versionSchema });
export const reassignTaskRequestSchema = z.object({
    version: versionSchema,
    ownerMemberId: uuidSchema,
    reason: reasonSchema,
});
export const TASK_COLLABORATOR_KINDS = ['COLLABORATOR', 'WATCHER'];
export const taskCollaboratorKindSchema = z.enum(TASK_COLLABORATOR_KINDS);
export const setCollaboratorRequestSchema = z.object({ kind: taskCollaboratorKindSchema });
export const checklistItemTitleSchema = z.string().trim().min(1).max(300);
export const createChecklistItemRequestSchema = z.object({ title: checklistItemTitleSchema });
export const updateChecklistItemRequestSchema = z
    .object({ title: checklistItemTitleSchema.optional(), isDone: z.boolean().optional() })
    .refine((value) => value.title !== undefined || value.isDone !== undefined, {
    message: 'Nothing to update',
});
export const taskCommentBodySchema = z.string().trim().min(1).max(10000);
export const createTaskCommentRequestSchema = z.object({ body: taskCommentBodySchema });
export const addDependencyRequestSchema = z.object({ dependsOnTaskId: uuidSchema });
export const taskStatusChangeSchema = z.object({
    fromStatus: taskStatusSchema.nullable(),
    toStatus: taskStatusSchema,
    note: z.string().nullable(),
    /** `null` = system transition (blocker resolution by another member is still attributed). */
    changedBy: z.string().nullable(),
    changedAt: isoDateTimeSchema,
});
export const taskOwnerChangeSchema = z.object({
    fromOwner: z.string().nullable(),
    toOwner: z.string(),
    reason: z.string().nullable(),
    changedBy: z.string(),
    changedAt: isoDateTimeSchema,
});
export const taskCollaboratorSchema = memberRef.extend({ kind: taskCollaboratorKindSchema });
export const checklistItemSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    isDone: z.boolean(),
    doneAt: isoDateTimeSchema.nullable(),
    doneBy: z.string().nullable(),
});
export const taskCommentSchema = z.object({
    id: uuidSchema,
    author: memberRef,
    /** Empty when deleted (the tombstone stays in the timeline). */
    body: z.string(),
    deleted: z.boolean(),
    createdAt: isoDateTimeSchema,
    canDelete: z.boolean(),
});
/** A related task the reader may not be allowed to open is reported without its title. */
export const taskDependencySchema = z.object({
    taskId: uuidSchema,
    number: z.number().int(),
    title: z.string().nullable(),
    status: taskStatusSchema,
    visible: z.boolean(),
});
export const taskPermissionsSchema = z.object({
    canEdit: z.boolean(),
    canChangeStatus: z.boolean(),
    canReassign: z.boolean(),
    canComment: z.boolean(),
    canManageCollaborators: z.boolean(),
    canOpenBlocker: z.boolean(),
});
export const BLOCKER_STATUSES = ['OPEN', 'RESOLVED', 'CANCELLED'];
export const blockerStatusSchema = z.enum(BLOCKER_STATUSES);
export const blockerSchema = z.object({
    id: uuidSchema,
    status: blockerStatusSchema,
    description: z.string(),
    /** Exactly one of task/topic (spec §6.5); titles are omitted when the reader cannot see them. */
    task: entityRef.extend({ title: z.string().nullable() }).nullable(),
    topic: entityRef.extend({ title: z.string().nullable() }).nullable(),
    blockedMember: memberRef,
    requestedFrom: memberRef.nullable(),
    requestedFromTeam: z.string().nullable(),
    sinceAt: isoDateTimeSchema,
    /** Effective response deadline: the explicit date, else opened time + SLA (own or workspace). */
    responseDueAt: isoDateTimeSchema,
    /** Blocker-specific SLA override; `null` = workspace default. */
    slaHours: z.number().int().nullable(),
    /** Response deadline has passed while the blocker is still open. */
    overdue: z.boolean(),
    escalationLevel: z.number().int(),
    lastEscalatedAt: isoDateTimeSchema.nullable(),
    resolutionNote: z.string().nullable(),
    resolvedAt: isoDateTimeSchema.nullable(),
    resolvedBy: z.string().nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
    canResolve: z.boolean(),
    /** The reporter side may withdraw a blocker; the member it waits on can only resolve it. */
    canCancel: z.boolean(),
    version: versionSchema,
});
export const blockerListSchema = z.object({ items: z.array(blockerSchema) });
export const BLOCKER_VIEWS = ['WAITING_ON_ME', 'MINE', 'ALL'];
export const blockerViewSchema = z.enum(BLOCKER_VIEWS);
export const blockerListQuerySchema = z.object({
    view: blockerViewSchema.default('ALL'),
    status: blockerStatusSchema.default('OPEN'),
});
export const openBlockerRequestSchema = z
    .object({
    description: z.string().trim().min(3).max(5000),
    requestedFromMemberId: uuidSchema.nullable().optional(),
    requestedFromTeam: z.string().trim().min(2).max(120).nullable().optional(),
    responseDueAt: isoDateTimeSchema.nullable().optional(),
    /** Overrides the workspace default SLA for this blocker. */
    slaHours: z.number().int().min(1).max(720).nullable().optional(),
})
    .refine((value) => (value.requestedFromMemberId !== undefined && value.requestedFromMemberId !== null) ||
    (value.requestedFromTeam !== undefined && value.requestedFromTeam !== null), {
    message: 'Say who is expected to respond (a member or a team)',
    path: ['requestedFromMemberId'],
});
export const resolveBlockerRequestSchema = z.object({
    version: versionSchema,
    resolutionNote: z.string().trim().min(3).max(5000),
});
export const cancelBlockerRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const taskDetailSchema = taskSummarySchema.extend({
    description: z.string().nullable(),
    acceptanceCriteria: z.string().nullable(),
    outcomeNote: z.string().nullable(),
    evidenceUrl: z.string().nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
    topic: entityRef.nullable(),
    decision: entityRef.nullable(),
    createdBy: z.string(),
    collaborators: z.array(taskCollaboratorSchema),
    checklist: z.array(checklistItemSchema),
    comments: z.array(taskCommentSchema),
    dependsOn: z.array(taskDependencySchema),
    blocks: z.array(taskDependencySchema),
    blockers: z.array(blockerSchema),
    statusHistory: z.array(taskStatusChangeSchema),
    ownerHistory: z.array(taskOwnerChangeSchema),
    /** Plain workflow moves available to the caller right now. */
    allowedTransitions: z.array(workflowTaskStatusSchema),
    canComplete: z.boolean(),
    canReopen: z.boolean(),
    canCancel: z.boolean(),
    canArchive: z.boolean(),
    /** Why completion is unavailable (e.g. an unfinished prerequisite), for the UI. */
    completionBlockedReason: z.string().nullable(),
    permissions: taskPermissionsSchema,
    updatedAt: isoDateTimeSchema,
    version: versionSchema,
});
//# sourceMappingURL=tasks.js.map